import { useEffect, useState } from 'react';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const POLL_INTERVAL_MS = 30_000; // re-check every 30 seconds

/**
 * MaintenanceBanner
 *
 * Polls GET /health every 30 seconds. If the backend returns
 * `maintenance: true` OR responds with a 503, this renders a full-screen
 * friendly overlay that blocks interaction until the backend comes back.
 *
 * When the backend recovers (health returns `maintenance: false`),
 * the overlay disappears automatically without needing a page refresh.
 */
export default function MaintenanceBanner() {
  const [isMaintenance, setIsMaintenance] = useState(false);

  useEffect(() => {
    let timer: number;

    async function checkHealth() {
      try {
        const res = await fetch(`${API}/health`, { cache: 'no-store' });
        if (res.status === 503) {
          setIsMaintenance(true);
        } else if (res.ok) {
          const json = await res.json().catch(() => ({}));
          setIsMaintenance(json.maintenance === true);
        }
      } catch {
        // Network error — don't show maintenance banner, just log
      }
      timer = window.setTimeout(checkHealth, POLL_INTERVAL_MS);
    }

    checkHealth();
    return () => window.clearTimeout(timer);
  }, []);

  if (!isMaintenance) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#FBF7F1] px-6 text-center"
    >
      {/* Pulsing logo-colour dot */}
      <div className="w-16 h-16 rounded-full bg-[#FFE5D4] flex items-center justify-center mb-6 shadow-inner">
        <span
          className="material-symbols-outlined text-[#ff795c]"
          style={{ fontSize: '32px', fontVariationSettings: "'FILL' 1" }}
         aria-hidden="true">
          rocket_launch
        </span>
      </div>

      <h1
        className="text-3xl font-bold text-[#ff795c] mb-3"
        style={{ fontFamily: "'Fraunces', serif" }}
      >
        We're scaling up for you!
      </h1>

      <p className="text-on-surface-variant max-w-md leading-relaxed mb-2">
        Learnex is experiencing extremely high demand right now. We're spinning
        up more capacity to serve everyone. We'll be back very soon — usually
        within 30 minutes.
      </p>

      <p className="text-sm text-on-surface-variant/70 mt-4">
        This page will automatically refresh when we're back online.
      </p>

      {/* Animated "checking" indicator */}
      <div className="mt-8 flex items-center gap-2 text-xs text-on-surface-variant">
        <span className="inline-block w-2 h-2 rounded-full bg-[#ff795c] animate-ping" />
        Checking status every 30 seconds...
      </div>
    </div>
  );
}
